import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Box, ArrowLeft, FileText } from 'lucide-react';
import BreadcrumbSchema from '../components/BreadcrumbSchema';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bg-dark">
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: 'https://printmindai.com/' },
          { name: 'Terms of Service', url: 'https://printmindai.com/terms' },
        ]}
      />
      {/* Navbar */}
      <nav className="border-b border-text-muted/10 bg-bg-dark/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => navigate('/')} className="flex items-center space-x-3 hover:opacity-80 transition">
              <div className="relative">
                <Brain className="w-8 h-8 text-primary" />
                <Box className="w-4 h-4 text-accent absolute -bottom-0.5 -right-0.5" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                PrintMind AI
              </span>
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-text-muted hover:text-text-primary transition text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-accent/15 rounded-xl flex items-center justify-center">
            <FileText className="w-6 h-6 text-accent" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-text-primary" data-testid="terms-title">Terms of Service</h1>
            <p className="text-text-muted text-sm mt-1">Last updated: April 5, 2026</p>
          </div>
        </div>

        <div className="prose prose-invert max-w-none space-y-8 text-text-secondary text-sm leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") govern your access to and use of PrintMind AI (the "Service"), operated by
              <strong className="text-text-primary"> Harbor Momentum Group LLC</strong> ("Company," "we," "us," or "our"),
              located at printmindai.com. By accessing or using the Service, you agree to be bound by these Terms. If you
              do not agree, you may not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">2. Description of Service</h2>
            <p>
              PrintMind AI is a cloud-based platform that provides AI-powered tools for 3D printing professionals, including:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li><strong className="text-text-primary">AI Support Engine:</strong> Automated generation of optimized tree support structures for 3D models.</li>
              <li><strong className="text-text-primary">Repair & Analysis:</strong> Automated detection and repair of mesh errors in STL files.</li>
              <li><strong className="text-text-primary">Vision Diagnosis:</strong> AI-powered analysis of 3D print failure photographs with slicer setting recommendations.</li>
              <li><strong className="text-text-primary">Business Quoting:</strong> Professional PDF quote generation with material cost estimation.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">3. Account Registration</h2>
            <p>To access certain features, you must create an account. You agree to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Provide accurate and complete registration information.</li>
              <li>Maintain the security of your account credentials.</li>
              <li>Promptly update any changes to your information.</li>
              <li>Accept responsibility for all activities under your account.</li>
            </ul>
            <p className="mt-3">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">4. Intellectual Property Rights</h2>
            <h3 className="text-base font-medium text-text-primary mb-2">4.1 Your Content</h3>
            <p>
              You retain <strong className="text-text-primary">full ownership and all intellectual property rights</strong> to
              any STL files, 3D models, images, or other content you upload to the Service ("User Content"). By uploading
              User Content, you grant us a limited, non-exclusive license to process, analyze, and store your files solely
              for the purpose of providing the requested services (analysis, repair, support generation, AI diagnosis, and quoting).
            </p>
            <p className="mt-3">
              We do <strong className="text-text-primary">not</strong>:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>Claim ownership of your uploaded 3D models or designs.</li>
              <li>Use your files to train machine learning models.</li>
              <li>Share, sell, license, or distribute your files to third parties.</li>
              <li>Access your files for any purpose other than providing the Service.</li>
            </ul>

            <h3 className="text-base font-medium text-text-primary mb-2 mt-4">4.2 Our Content</h3>
            <p>
              The Service, including its design, features, algorithms, documentation, and branding, is owned by Harbor Momentum
              Group LLC and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify,
              distribute, or reverse-engineer any part of the Service without our prior written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">5. Subscription Plans and Payments</h2>
            <h3 className="text-base font-medium text-text-primary mb-2">5.1 Free Tier</h3>
            <p>
              The Free plan provides limited access to the Service with usage quotas. Free accounts are subject to the same
              Terms and may be upgraded at any time.
            </p>

            <h3 className="text-base font-medium text-text-primary mb-2 mt-4">5.2 Paid Plans (Pro & Studio)</h3>
            <p>
              Paid subscriptions are billed monthly through Stripe. By subscribing, you authorize recurring charges to your
              payment method. You may cancel your subscription at any time; cancellation takes effect at the end of the
              current billing period.
            </p>

            <h3 className="text-base font-medium text-text-primary mb-2 mt-4">5.3 Refunds</h3>
            <p>
              Subscription fees are generally non-refundable. We may, at our sole discretion, issue refunds or credits for
              service disruptions or billing errors. Contact us at <a href="mailto:support@printmind.ai" className="text-primary hover:underline">support@printmind.ai</a> for billing inquiries.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">6. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Upload content that infringes on the intellectual property rights of others.</li>
              <li>Upload malicious files, viruses, or harmful code.</li>
              <li>Attempt to gain unauthorized access to the Service or other users' accounts.</li>
              <li>Use the Service for any illegal or unauthorized purpose.</li>
              <li>Interfere with or disrupt the integrity or performance of the Service.</li>
              <li>Scrape, crawl, or use automated tools to extract data from the Service without permission.</li>
              <li>Resell or redistribute access to the Service without authorization.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">7. Service Availability and Disclaimers</h2>
            <p>
              The Service is provided on an <strong className="text-text-primary">"as is"</strong> and <strong className="text-text-primary">"as available"</strong> basis.
              We make no warranties, express or implied, regarding:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>The accuracy or reliability of AI-generated analyses, repair results, or support structures.</li>
              <li>Uninterrupted or error-free access to the Service.</li>
              <li>The suitability of generated supports for your specific 3D printing hardware or materials.</li>
            </ul>
            <p className="mt-3">
              <strong className="text-text-primary">Important:</strong> PrintMind AI provides AI-assisted tools to help optimize
              your 3D printing workflow. AI analysis and generated supports are recommendations, not guarantees. Always review
              generated outputs before printing. We are not responsible for print failures, material waste, or equipment damage
              resulting from the use of our generated files.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">8. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, Harbor Momentum Group LLC shall not be liable for any
              indirect, incidental, special, consequential, or punitive damages, including but not limited to: loss of profits,
              data, or business opportunities, whether arising from contract, tort, or otherwise, even if we have been advised
              of the possibility of such damages.
            </p>
            <p className="mt-3">
              Our total liability for any claims arising from or related to the Service shall not exceed the total amount
              paid by you to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">9. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Harbor Momentum Group LLC, its officers, directors, employees, and
              agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the
              Service, your violation of these Terms, or your infringement of any third-party rights.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">10. Termination</h2>
            <p>
              We may suspend or terminate your access to the Service at any time, with or without cause, and with or without
              notice. Upon termination, your right to use the Service ceases immediately. Sections that by their nature should
              survive termination (including intellectual property, limitation of liability, and indemnification) shall survive.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">11. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States,
              without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Delaware.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">12. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Material changes will be communicated via email or
              a prominent notice on the Service. Your continued use after changes constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-text-primary mb-3">13. Contact Us</h2>
            <p>For any questions about these Terms of Service, please contact us:</p>
            <div className="mt-3 p-4 bg-bg-light rounded-xl border border-text-muted/20">
              <p className="font-medium text-text-primary">Harbor Momentum Group LLC</p>
              <p>d/b/a PrintMind AI</p>
              <p className="mt-2">
                Email: <a href="mailto:support@printmind.ai" className="text-primary hover:underline">support@printmind.ai</a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
